import axios from 'axios';
import { db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { searchEventbriteEvents, getEventbriteEventById, eventbriteRequest } from './eventbriteProxy';

// API configuration - DO NOT hardcode actual API keys here in production!
// Use environment variables or a secure server-side solution
const API_CONFIG = {
  baseUrl: 'https://www.eventbriteapi.com/v3',
  // The token will be loaded from a secure configuration later
  token: ''
};

// Event interfaces
export interface EventbriteEvent {
  id: string;
  name: {
    text: string;
    html: string;
  };
  description?: {
    text: string;
    html: string;
  };
  url: string;
  start: {
    timezone: string;
    local: string;
    utc: string;
  };
  end: {
    timezone: string;
    local: string;
    utc: string;
  };
  organization_id: string;
  created: string;
  changed: string;
  published?: string;
  capacity?: number;
  capacity_is_custom?: boolean;
  status: string;
  currency: string;
  listed?: boolean;
  shareable?: boolean;
  online_event: boolean;
  tx_time_limit?: number;
  hide_start_date: boolean;
  hide_end_date: boolean;
  locale: string;
  is_locked: boolean;
  privacy_setting: string;
  is_series: boolean;
  is_series_parent: boolean;
  inventory_tier: string;
  is_reserved_seating: boolean;
  show_pick_a_seat: boolean;
  show_seatmap_thumbnail: boolean;
  show_colors_in_seatmap_thumbnail: boolean;
  source?: string;
  is_free: boolean;
  version?: string;
  summary?: string;
  logo_id?: string;
  organizer_id: string;
  venue_id?: string;
  category_id?: string;
  subcategory_id?: string;
  format_id?: string;
  resource_uri?: string;
  is_externally_ticketed?: boolean;
  logo?: {
    crop_mask: any;
    original: {
      url: string;
      width: number;
      height: number;
    };
    id: string;
    url: string;
    aspect_ratio: string;
    edge_color?: string;
    edge_color_set?: boolean;
  };
  venue?: {
    address: {
      address_1?: string;
      address_2?: string;
      city?: string;
      region?: string;
      postal_code?: string;
      country?: string;
      latitude?: string;
      longitude?: string;
      localized_address_display?: string;
      localized_area_display?: string;
      localized_multi_line_address_display?: string[];
    };
    resource_uri?: string;
    id?: string;
    name?: string;
    latitude?: string;
    longitude?: string;
  };
}

export interface NormalizedEvent {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  url: string;
  location: {
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    latitude?: string;
    longitude?: string;
  };
  venue?: string;
  isOnline: boolean;
  isFree: boolean;
  organizerName?: string;
  imageUrl?: string;
  categories?: string[];
  tags?: string[];
  source: string;
  sourceId: string;
  rawData: any;
}

// The main service object
const eventbriteService = {
  setApiToken(token: string) {
    API_CONFIG.token = token;
    console.log("API token updated:", token.substring(0, 5) + "...");
  },
  
  getApiToken(): string {
    return API_CONFIG.token;
  },
  
  // Search events using multiple criteria
  searchEvents: async (params: {
    q?: string;
    location?: string;
    city?: string;
    startDate?: Date | null;
    endDate?: Date | null;
    categories?: string[];
    onlineEvents?: boolean;
    page?: number;
    pageSize?: number;
    latitude?: number;
    longitude?: number;
    radius?: number;
  }): Promise<{ events: NormalizedEvent[]; pagination: any }> => {
    try {
      const token = eventbriteService.getApiToken();
      if (!token) {
        throw new Error("API token is not set");
      }
      
      console.log("Searching events with token:", token.substring(0, 5) + "...");
      
      // Simplified approach - use direct parameter mapping for the events endpoint
      const apiParams: Record<string, any> = {
        // Basic pagination
        page: params.page || 1,
        page_size: params.pageSize || 25,
      };
      
      // Add search term as a query parameter if provided
      if (params.q) {
        apiParams.q = params.q;
      }
      
      // Add location parameter if provided
      if (params.city) {
        apiParams.location = params.city;
      } else if (params.location) {
        apiParams.location = params.location;
      }
      
      // Add date filters
      if (params.startDate) {
        const formattedDate = params.startDate.toISOString().split('T')[0]; // YYYY-MM-DD
        apiParams.start_date = formattedDate;
      }
      
      if (params.endDate) {
        const formattedDate = params.endDate.toISOString().split('T')[0]; // YYYY-MM-DD
        apiParams.end_date = formattedDate;
      }
      
      // Expand venue, organizer and ticket details
      apiParams.expand = "venue,organizer,ticket_availability";
      
      // Use the simplified approach with the events endpoint
      const response = await eventbriteRequest({
        endpoint: 'events',
        params: apiParams,
        token
      });
      
      console.log("API response received:", response ? "success" : "empty");
      
      // Check if we have events in the response
      if (!response || !response.events) {
        console.error("No events data in response");
        return { events: [], pagination: { object_count: 0 } };
      }
      
      // Normalize the API response
      const normalizedEvents = response.events.map(
        (event: any): NormalizedEvent => {
          return eventbriteService.normalizeEvent(event);
        }
      );
      
      console.log(`Found ${normalizedEvents.length} events`);

      return {
        events: normalizedEvents,
        pagination: response.pagination || { object_count: normalizedEvents.length }
      };
    } catch (error: any) {
      console.error('Error searching Eventbrite events:', error.message);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      }
      throw error;
    }
  },

  // Get event details by ID
  getEventById: async (eventId: string): Promise<NormalizedEvent> => {
    try {
      const token = eventbriteService.getApiToken();
      if (!token) {
        throw new Error("API token is not set");
      }
      
      const response = await getEventbriteEventById(token, eventId);
      
      return eventbriteService.normalizeEvent(response);
    } catch (error: any) {
      console.error(`Error getting Eventbrite event ${eventId}:`, error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  },

  // Save event to Firestore
  saveEventToFirestore: async (event: NormalizedEvent): Promise<string> => {
    try {
       // First check if event already exists
       const eventRef = collection(db, 'events');
       const q = query(
          eventRef,
          where('sourceId', '==', event.id),
          where('source', '==', 'eventbrite')
       );
       const querySnapshot = await getDocs(q);

       if (!querySnapshot.empty) {
          return querySnapshot.docs[0].id; // Return existing ID
       }

       // Add new event using backend API
       const { createApiUrl } = await import('../utils/apiConfig');
       const apiUrl = createApiUrl('/api/events');

       const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
             'Content-Type': 'application/json',
          },
          body: JSON.stringify({
             title: event.title,
             description: event.description,
             startDate: event.startDate,
             endDate: event.endDate,
             location: event.location,
             isPublic: false, // Default for imported events
             userId: 'eventbrite-import', // Default user for imports
             // Include Eventbrite-specific fields
             sourceId: event.id,
             source: 'eventbrite',
             sourceUrl: event.url,
             imageUrl: event.imageUrl,
          }),
       });

       const result = await response.json();

       if (!response.ok) {
          throw new Error(result.error || 'Failed to create event from Eventbrite');
       }

       return result.eventId;
    } catch (error) {
      console.error('Error saving event to Firestore:', error);
      throw error;
    }
  },

  // Helper to normalize Eventbrite event data
  normalizeEvent: (event: any): NormalizedEvent => {
    // Create a standardized event object from Eventbrite data
    try {
      return {
        id: event.id,
        title: event.name?.text || event.name || 'Untitled Event',
        description: event.description?.text || event.description?.html || event.description || '',
        startDate: event.start?.local || event.start?.utc || '',
        endDate: event.end?.local || event.end?.utc || '',
        url: event.url || '',
        location: {
          name: event.venue?.name || '',
          address: event.venue?.address?.localized_address_display || 
                 event.venue?.address?.address_1 || '',
          city: event.venue?.address?.city || '',
          state: event.venue?.address?.region || '',
          postalCode: event.venue?.address?.postal_code || '',
          country: event.venue?.address?.country || '',
          latitude: event.venue?.address?.latitude || '',
          longitude: event.venue?.address?.longitude || ''
        },
        venue: event.venue?.name || '',
        isOnline: event.online_event || false,
        isFree: event.is_free || false,
        organizerName: event.organizer?.name || '',
        imageUrl: event.logo?.url || event.logo?.original?.url || '',
        categories: event.categories?.map((cat: any) => cat.name) || [],
        tags: event.tags?.map((tag: any) => tag.name) || [],
        source: 'eventbrite',
        sourceId: event.id,
        rawData: event
      };
    } catch (error) {
      console.error('Error normalizing event data:', error);
      // Return a minimal event object if normalization fails
      return {
        id: event.id || 'unknown',
        title: event.name?.text || 'Error Processing Event',
        description: 'Error processing event data',
        startDate: '',
        endDate: '',
        url: event.url || '',
        location: {
          name: '',
          address: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          latitude: '',
          longitude: ''
        },
        isOnline: false,
        isFree: false,
        source: 'eventbrite',
        sourceId: event.id || 'unknown',
        rawData: event
      };
    }
  }
};

export default eventbriteService;
